import React, { useState, useEffect, useCallback } from 'react';
import { BACKEND_URL } from '../config';
import { fetchWithOrg } from '../utils/api';
import { toast } from 'sonner';
import {
  Heartbeat, CircleNotch, Globe, ShieldCheck, Lightning, Clock,
  ArrowClockwise, Warning, CheckCircle, XCircle, ArrowsClockwise,
} from '@phosphor-icons/react';

const HEALTH_CONFIG = {
  healthy: { bg: 'bg-emerald-500/15', text: 'text-emerald-400', border: 'border-emerald-500/30', label: 'Healthy', dot: 'bg-emerald-400' },
  degraded: { bg: 'bg-yellow-500/15', text: 'text-yellow-400', border: 'border-yellow-500/30', label: 'Degraded', dot: 'bg-yellow-400' },
  unreachable: { bg: 'bg-red-500/15', text: 'text-red-400', border: 'border-red-500/30', label: 'Unreachable', dot: 'bg-red-400' },
  unknown: { bg: 'bg-[var(--surface-2)]', text: 'text-[#718096]', border: 'border-[var(--border-subtle)]', label: 'Unknown', dot: 'bg-[#718096]' },
};

const STATUS_LABELS = {
  active: { text: 'text-emerald-400', label: 'Active' },
  approved: { text: 'text-cyan-400', label: 'Approved' },
  suspended: { text: 'text-orange-400', label: 'Suspended' },
  inactive: { text: 'text-[#718096]', label: 'Inactive' },
};

function ExternalAgentHealth() {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingAll, setCheckingAll] = useState(false);
  const [checkingOne, setCheckingOne] = useState(null);

  const fetchOverview = useCallback(async () => {
    try {
      const res = await fetchWithOrg(`${BACKEND_URL}/api/federation/health/overview`);
      if (res.ok) setOverview(await res.json());
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchOverview(); }, [fetchOverview]);

  const handleCheckAll = async () => {
    setCheckingAll(true);
    try {
      const res = await fetchWithOrg(`${BACKEND_URL}/api/federation/health/check-all`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Checked ${data.checked} agents: ${data.healthy} healthy, ${data.unhealthy} unhealthy`);
        fetchOverview();
      } else {
        toast.error('Batch health check failed');
      }
    } catch { toast.error('Batch check failed'); } finally { setCheckingAll(false); }
  };

  const handleCheckOne = async (federationId, name) => {
    setCheckingOne(federationId);
    try {
      const res = await fetchWithOrg(`${BACKEND_URL}/api/federation/health/${federationId}`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        const status = data.healthy ? 'healthy' : 'unreachable';
        const latency = data.duration_ms ? `${data.duration_ms}ms` : '';
        toast[data.healthy ? 'success' : 'error'](`${name}: ${status} ${latency}`);
        fetchOverview();
      }
    } catch { toast.error(`Health check failed for ${name}`); } finally { setCheckingOne(null); }
  };

  const health = overview?.health || {};
  const agents = overview?.agents || [];
  const total = overview?.total || 0;

  return (
    <div data-testid="external-agent-health" className="min-h-screen bg-[var(--surface-0)] px-6 py-8 lg:px-10">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-start justify-between mb-8">
          <div>
            <h1 className="text-2xl font-semibold text-white tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Agent Health Monitor
            </h1>
            <p className="text-sm text-[#718096] mt-1">
              Real-time health status of external agents in the federation
            </p>
          </div>
          <div className="flex items-center gap-3">
            {overview?.last_global_check && (
              <span className="text-xs text-[#718096]">
                <Clock size={12} className="inline mr-1" />
                Last check: {formatRelative(overview.last_global_check)}
              </span>
            )}
            <button
              data-testid="check-all-btn"
              onClick={handleCheckAll}
              disabled={checkingAll || loading}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white text-black text-sm font-medium hover:bg-gray-100 transition-colors disabled:opacity-50"
            >
              {checkingAll ? (
                <CircleNotch size={16} className="animate-spin" />
              ) : (
                <ArrowsClockwise size={16} weight="bold" />
              )}
              {checkingAll ? 'Checking...' : 'Check All'}
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <CircleNotch size={24} className="animate-spin text-[#718096]" />
          </div>
        ) : (
          <>
            {/* Overview Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-8">
              <OverviewCard label="Healthy" value={health.healthy || 0} total={total} config={HEALTH_CONFIG.healthy} />
              <OverviewCard label="Degraded" value={health.degraded || 0} total={total} config={HEALTH_CONFIG.degraded} />
              <OverviewCard label="Unreachable" value={health.unreachable || 0} total={total} config={HEALTH_CONFIG.unreachable} />
              <OverviewCard label="Unknown" value={health.unknown || 0} total={total} config={HEALTH_CONFIG.unknown} />
              <div className="p-3 rounded-xl bg-[var(--surface-1)] border border-[var(--border-subtle)] text-center">
                <p className="text-lg font-semibold text-white">{overview?.healthy_pct ?? 0}%</p>
                <p className="text-[10px] text-[#718096] uppercase tracking-wider">Health Rate</p>
              </div>
              <div className="p-3 rounded-xl bg-[var(--surface-1)] border border-[var(--border-subtle)] text-center">
                <p className="text-lg font-semibold text-white">{overview?.avg_latency_ms != null ? `${overview.avg_latency_ms}` : '—'}</p>
                <p className="text-[10px] text-[#718096] uppercase tracking-wider">Avg Latency ms</p>
              </div>
              <div className="p-3 rounded-xl bg-[var(--surface-1)] border border-[var(--border-subtle)] text-center">
                <p className="text-lg font-semibold text-white">{total}</p>
                <p className="text-[10px] text-[#718096] uppercase tracking-wider">Total Agents</p>
              </div>
            </div>

            {/* Agent Health Table */}
            {agents.length === 0 ? (
              <div className="text-center py-16 bg-[var(--surface-1)] rounded-xl border border-[var(--border-subtle)]">
                <Globe size={36} className="mx-auto text-[#5C6778] mb-2" />
                <p className="text-sm text-[#718096]">No agents to monitor</p>
                <p className="text-xs text-[#5C6778] mt-1">External agents will appear here once approved or activated</p>
              </div>
            ) : (
              <div className="bg-[var(--surface-1)] rounded-xl border border-[var(--border-subtle)] overflow-hidden">
                {/* Table Header */}
                <div className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr_1fr_auto] gap-4 px-5 py-3 border-b border-[var(--border-subtle)] text-[10px] uppercase tracking-wider text-[#718096] font-medium">
                  <span>Agent</span>
                  <span>Health</span>
                  <span>Latency</span>
                  <span>Last Checked</span>
                  <span>Status</span>
                  <span>Trust</span>
                  <span className="text-right">Actions</span>
                </div>

                {/* Table Rows */}
                {agents.map((agent) => (
                  <AgentHealthRow
                    key={agent.federation_id}
                    agent={agent}
                    onCheck={() => handleCheckOne(agent.federation_id, agent.name)}
                    checking={checkingOne === agent.federation_id}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function OverviewCard({ label, value, total, config }) {
  return (
    <div className={`p-3 rounded-xl border text-center ${config.bg} ${config.border}`}>
      <p className={`text-lg font-semibold ${config.text}`}>{value}</p>
      <p className="text-[10px] text-[#718096] uppercase tracking-wider">{label}</p>
    </div>
  );
}

function AgentHealthRow({ agent, onCheck, checking }) {
  const hc = HEALTH_CONFIG[agent.health_status] || HEALTH_CONFIG.unknown;
  const sc = STATUS_LABELS[agent.status] || STATUS_LABELS.inactive;
  const failures = agent.consecutive_failures || 0;
  const isSuspended = agent.status === 'suspended';
  const isAutoSuspended = isSuspended && agent.suspension_reason === 'health_check_failures';

  return (
    <div
      data-testid={`health-row-${agent.federation_id}`}
      className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr_1fr_auto] gap-4 px-5 py-3 border-b border-[var(--border-subtle)] last:border-b-0 items-center hover:bg-[rgba(255,255,255,0.02)] transition-colors"
    >
      {/* Agent */}
      <div className="min-w-0">
        <p className="text-sm font-medium text-white truncate">{agent.name}</p>
        <div className="flex items-center gap-2 text-[10px] text-[#718096]">
          {agent.developer_name && <span>{agent.developer_name}</span>}
          <span className="font-mono">{agent.federation_id}</span>
        </div>
        {/* Auto-suspend/recover indicators */}
        {isAutoSuspended && (
          <p className="text-[10px] text-orange-400 mt-0.5 flex items-center gap-1">
            <Warning size={10} />
            Auto-suspended after 3 failed health checks
          </p>
        )}
        {agent.prev_health_status === 'unreachable' && agent.health_status === 'healthy' && (
          <p className="text-[10px] text-emerald-400 mt-0.5 flex items-center gap-1">
            <CheckCircle size={10} />
            Recovered automatically on successful health check
          </p>
        )}
      </div>

      {/* Health */}
      <div className="flex items-center gap-1.5">
        <span className={`w-2 h-2 rounded-full ${hc.dot}`} />
        <span className={`text-xs font-medium ${hc.text}`}>{hc.label}</span>
        {failures > 0 && (
          <span className="text-[10px] text-red-400 ml-1">({failures}x)</span>
        )}
      </div>

      {/* Latency */}
      <div>
        {agent.latency_ms != null ? (
          <span className={`text-xs font-mono ${
            agent.latency_ms < 500 ? 'text-emerald-400' :
            agent.latency_ms < 2000 ? 'text-yellow-400' : 'text-red-400'
          }`}>
            {agent.latency_ms}ms
          </span>
        ) : (
          <span className="text-xs text-[#5C6778]">—</span>
        )}
      </div>

      {/* Last Checked */}
      <div>
        {agent.health_last_checked ? (
          <div>
            <p className="text-xs text-[#A0AEC0]">{formatRelative(agent.health_last_checked)}</p>
            {agent.last_successful_check && agent.last_successful_check !== agent.health_last_checked && (
              <p className="text-[10px] text-[#718096]">Last OK: {formatRelative(agent.last_successful_check)}</p>
            )}
          </div>
        ) : (
          <span className="text-xs text-[#5C6778]">Never</span>
        )}
      </div>

      {/* Status */}
      <span className={`text-xs font-medium ${sc.text}`}>{sc.label}</span>

      {/* Trust */}
      <span className="text-xs text-[#A0AEC0]">
        <ShieldCheck size={10} className="inline mr-0.5" />
        {agent.trust_class?.replace(/_/g, ' ')}
      </span>

      {/* Actions */}
      <div className="flex justify-end">
        <button
          data-testid={`check-health-${agent.federation_id}`}
          onClick={onCheck}
          disabled={checking}
          className="flex items-center gap-1 px-2 py-1 rounded-lg bg-[var(--surface-2)] border border-[var(--border-subtle)] text-[#A0AEC0] text-xs hover:text-white hover:border-[rgba(255,255,255,0.15)] transition-colors disabled:opacity-50"
          title="Run health check"
        >
          {checking ? (
            <CircleNotch size={12} className="animate-spin" />
          ) : (
            <Heartbeat size={12} />
          )}
          Check
        </button>
      </div>
    </div>
  );
}

function formatRelative(isoString) {
  if (!isoString) return '';
  try {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDay = Math.floor(diffHr / 24);
    return `${diffDay}d ago`;
  } catch {
    return '';
  }
}

export default ExternalAgentHealth;
